import { useState } from "react";
import Header from "../../components/feature/Header";
import Footer from "../../components/feature/Footer";
import { Link } from "react-router-dom";

const projectTypes = ["Residential — New Construction", "Residential — Renovation", "Multi-Family / Development", "Hospitality — Hotel or Resort", "Hospitality — Restaurant or Bar", "Commercial / Mixed-Use", "Other"];
const budgetRanges = ["Under $250K", "$250K – $500K", "$500K – $1M", "$1M – $3M", "$3M – $10M", "$10M+", "Not sure yet"];
const timelines = ["Ready to start now", "Within 3 months", "3–6 months", "6–12 months", "Exploring / planning stage"];

const inputClass = "w-full border border-arch-border rounded-md px-4 py-3 text-sm text-arch-charcoal font-light focus:outline-none focus:border-arch-charcoal transition-colors bg-arch-cream placeholder-arch-light";

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const msg = (form.querySelector("[name=message]") as HTMLTextAreaElement)?.value || "";
    if (msg.length > 500) return;
    setLoading(true);
    const data = new FormData(form);
    const encoded = new URLSearchParams();
    data.forEach((val, key) => encoded.append(key, val as string));
    try {
      await fetch("https://readdy.ai/api/form/d6srmlid1d31f3q965bg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encoded.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-arch-cream font-sans">
      <Header />
      <main>
        {/* Page header */}
        <section className="pt-36 pb-14 px-8 lg:px-14 bg-white border-b border-arch-border">
          <p className="text-arch-light text-xs tracking-[0.2em] uppercase mb-4">Let&apos;s Talk</p>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <h1 className="font-serif text-5xl lg:text-6xl text-arch-charcoal font-light leading-tight">
              Request a<br />Consultation
            </h1>
            <p className="text-arch-gray text-sm font-light leading-relaxed max-w-sm">
              Initial consultations are complimentary. We meet at our Santa Monica studio or via video call to discuss your project scope, site, timeline, and vision.
            </p>
          </div>
        </section>

        {/* Contact grid */}
        <section className="py-16 lg:py-20 px-8 lg:px-14 bg-arch-cream">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">

            {/* Left — Info + Trust */}
            <div className="lg:col-span-2 space-y-8">
              {/* Studio Info */}
              <div className="bg-white rounded-lg border border-arch-border p-7">
                <p className="text-arch-light text-xs tracking-[0.2em] uppercase mb-6">Studio 1323</p>
                <div className="space-y-5">
                  {[
                    { icon: "ri-map-pin-2-line", label: "Address", value: "1323 Montana Avenue, Suite 200\nSanta Monica, CA 90403" },
                    { icon: "ri-phone-line", label: "Phone", value: "(310) 555-1323" },
                    { icon: "ri-mail-line", label: "Email", value: "hello@studio1323.com" },
                    { icon: "ri-time-line", label: "Hours", value: "Mon–Fri: 9AM – 6PM\nSat: By appointment" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-4">
                      <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 border border-arch-border rounded-md">
                        <i className={`${item.icon} text-arch-charcoal text-base`} />
                      </div>
                      <div>
                        <p className="text-arch-light text-xs uppercase tracking-wider mb-1">{item.label}</p>
                        <p className="text-arch-charcoal text-sm font-light whitespace-pre-line">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Service Areas */}
              <div className="bg-white rounded-lg border border-arch-border p-7">
                <p className="text-arch-light text-xs tracking-[0.2em] uppercase mb-4">Service Areas</p>
                <div className="flex flex-wrap gap-2">
                  {["Santa Monica", "Los Angeles", "Brentwood", "Pacific Palisades", "Malibu", "Venice", "Beverly Hills", "Culver City", "West Hollywood", "El Segundo"].map((area) => (
                    <span key={area} className="border border-arch-border text-arch-gray text-xs px-3 py-1.5 rounded-full font-light">
                      {area}
                    </span>
                  ))}
                </div>
              </div>

              {/* Social */}
              <div>
                <p className="text-arch-light text-xs tracking-[0.2em] uppercase mb-4">Follow</p>
                <div className="flex items-center gap-3">
                  {[
                    { icon: "ri-instagram-line", label: "Instagram" },
                    { icon: "ri-linkedin-line", label: "LinkedIn" },
                    { icon: "ri-pinterest-line", label: "Pinterest" },
                  ].map((s) => (
                    <a key={s.label} href="#" rel="nofollow" className="w-10 h-10 flex items-center justify-center border border-arch-border rounded-full text-arch-gray hover:text-arch-charcoal hover:border-arch-charcoal transition-all cursor-pointer" aria-label={s.label}>
                      <i className={`${s.icon} text-base`} />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Right — Form */}
            <div className="lg:col-span-3 bg-white p-8 lg:p-10 rounded-lg border border-arch-border">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-14 h-14 flex items-center justify-center border border-arch-border rounded-full mb-6">
                    <i className="ri-check-line text-arch-charcoal text-2xl" />
                  </div>
                  <h3 className="font-serif text-2xl text-arch-charcoal font-light mb-3">Message Received</h3>
                  <p className="text-arch-gray text-sm font-light max-w-xs leading-relaxed mb-8">
                    Thank you for reaching out. We&apos;ll be in touch within one business day to schedule your complimentary consultation.
                  </p>
                  <Link to="/projects" className="text-arch-gray text-xs uppercase tracking-wider underline cursor-pointer">
                    Browse Our Projects
                  </Link>
                </div>
              ) : (
                <>
                  <h2 className="font-serif text-2xl text-arch-charcoal font-light mb-2">Request a Consultation</h2>
                  <p className="text-arch-gray text-xs font-light mb-7">All fields marked * are required. Initial consultation is complimentary.</p>
                  <form id="contact-page-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="text-arch-gray text-xs tracking-wider uppercase block mb-1.5">Full Name *</label>
                        <input type="text" name="name" required placeholder="Jane Smith" className={inputClass} />
                      </div>
                      <div>
                        <label className="text-arch-gray text-xs tracking-wider uppercase block mb-1.5">Email *</label>
                        <input type="email" name="email" required placeholder="jane@example.com" className={inputClass} />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="text-arch-gray text-xs tracking-wider uppercase block mb-1.5">Phone</label>
                        <input type="tel" name="phone" placeholder="(310) 000-0000" className={inputClass} />
                      </div>
                      <div>
                        <label className="text-arch-gray text-xs tracking-wider uppercase block mb-1.5">Project Type *</label>
                        <select name="project_type" required className={`${inputClass} cursor-pointer`}>
                          <option value="">Select type...</option>
                          {projectTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="text-arch-gray text-xs tracking-wider uppercase block mb-1.5">Budget Range</label>
                        <select name="budget_range" className={`${inputClass} cursor-pointer`}>
                          <option value="">Select range...</option>
                          {budgetRanges.map((b) => <option key={b} value={b}>{b}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-arch-gray text-xs tracking-wider uppercase block mb-1.5">Timeline</label>
                        <select name="timeline" className={`${inputClass} cursor-pointer`}>
                          <option value="">Select timeline...</option>
                          {timelines.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="text-arch-gray text-xs tracking-wider uppercase block mb-1.5">Project Location</label>
                      <input type="text" name="location" placeholder="Santa Monica, CA" className={inputClass} />
                    </div>
                    <div>
                      <label className="text-arch-gray text-xs tracking-wider uppercase block mb-1.5">
                        Project Description *
                        <span className={`ml-2 font-normal normal-case tracking-normal ${charCount > 450 ? "text-red-500" : ""}`}>({charCount}/500)</span>
                      </label>
                      <textarea name="message" required rows={5} maxLength={500} placeholder="Describe your project scope, site conditions, vision, and any requirements..." onChange={(e) => setCharCount(e.target.value.length)} className={`${inputClass} resize-none`} />
                    </div>
                    <button type="submit" disabled={loading} className="w-full bg-arch-charcoal text-white py-4 text-xs tracking-[0.18em] uppercase font-semibold hover:bg-arch-black transition-all rounded-md cursor-pointer whitespace-nowrap disabled:opacity-60">
                      {loading ? "Sending..." : "Send Consultation Request"}
                    </button>
                    <p className="text-arch-light text-xs text-center font-light">
                      Free initial consultation · No commitment required · Response within 1 business day
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="bg-white border-t border-arch-border">
          <div className="px-8 lg:px-14 py-10">
            <p className="text-arch-light text-xs tracking-[0.2em] uppercase mb-4">Find Us</p>
            <h2 className="font-serif text-2xl text-arch-charcoal font-light mb-6">
              1323 Montana Avenue, Suite 200 &nbsp;·&nbsp; Santa Monica, CA 90403
            </h2>
          </div>
          <div className="w-full h-96 lg:h-[480px]">
            <iframe
              title="Studio 1323 Architecture Firm — Santa Monica Office Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d26446.51777!2d-118.5106!3d34.0195!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2a4cec2910019%3A0xb4170b5e238487c0!2sSanta%20Monica%2C%20CA%2090403!5e0!3m2!1sen!2sus!4v1710000000000!5m2!1sen!2sus"
              className="w-full h-full border-0 grayscale"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
